import { useMemo } from "react";
import { differenceInCalendarDays, format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Sparkles } from "lucide-react";

import { Column, DataTable } from "@/components/table/DataTable";
import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import {
  pessoaAcessoKeys,
  pessoaAcessoQueries,
} from "@/services/api/queries/pessoa-acesso.queries";
import {
  PessoaAcessoFiltrosCriteria,
  PessoaAcessoFiltrosOptions,
  PessoaAcessoRowDTO,
} from "@/types/pessoa-acesso.type";

import { Person } from "@/components/shared/person";

/** `dia` vem como "YYYY-MM-DD" (coluna `date`); parseado assim, o `new Date` cai em UTC e retrocede um dia em fuso negativo (ex.: -03:00). */
function parseDia(dia: string): Date {
  return new Date(`${dia}T00:00:00`);
}

/** "Hoje" / "Ontem" pros dias mais recentes; "há N dias/semanas/meses" pro resto (ptBR). */
function relativeLabel(dia: string): string {
  const data = parseDia(dia);
  const diff = differenceInCalendarDays(new Date(), data);
  if (diff === 0) return "Hoje";
  if (diff === 1) return "Ontem";
  return formatDistanceToNow(data, { addSuffix: true, locale: ptBR });
}

export function AcessosDataTable() {
  const { tableProps, isError, invalidate } = useServerDataTable<
    PessoaAcessoRowDTO,
    PessoaAcessoFiltrosOptions,
    PessoaAcessoFiltrosCriteria
  >({
    queryOptions: pessoaAcessoQueries.list,
    filters: pessoaAcessoQueries.filtros,
    invalidateKey: pessoaAcessoKeys.list,
  });

  const columns = useMemo<Column<PessoaAcessoRowDTO>[]>(
    () => [
      {
        key: "pessoa.nome",
        title: "Usuário",
        width: "min-w-[240px]",
        render: (row) => (
          <Person.Root pessoa={row.acessadoPor} asClickable className="flex items-center gap-2">
            <Person.Avatar />
            <Person.Name className="text-sm" />
            {row.primeiroAcesso && (
              <span
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400"
                title="Primeiro acesso registrado dessa pessoa"
              >
                <Sparkles className="h-2.5 w-2.5" />
                Primeiro acesso
              </span>
            )}
          </Person.Root>
        ),
        raw: (row) => row.acessadoPor.nome,
      },
      {
        key: "pessoa.email",
        title: "E-mail",
        width: "min-w-[220px]",
        render: (row) => (
          <span className="text-xs text-muted-foreground">{row.acessadoPor.email}</span>
        ),
        raw: (row) => row.acessadoPor.email || "",
      },
      {
        key: "pessoaAcessoDia.dia",
        title: "Data",
        sortable: true,
        width: "min-w-[140px]",
        render: (row) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {relativeLabel(row.dia)}
          </span>
        ),
        cellTitle: (row) => format(parseDia(row.dia), "dd/MM/yyyy"),
        raw: (row) => row.dia,
      },
    ],
    [],
  );

  return (
    <DataTable
      {...tableProps}
      storageKey="pessoa-acessos-v1"
      columns={columns}
      defaultSortColumn="pessoaAcessoDia.dia"
      defaultSortOrder="DESC"
      searchPlaceholder="Pesquisar por nome ou e-mail..."
      emptyMessage={
        isError
          ? "Não foi possível carregar os acessos. Tente recarregar."
          : "Nenhum acesso registrado."
      }
      isError={isError}
      onRetry={invalidate}
      exportFilename="historico-acessos"
    />
  );
}
